import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw, Search, Filter, MessageSquare, Phone, ArrowUpAZ, ArrowDownZA, CheckCircle, Clock, Send, TrendingUp } from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Toast,
  ToastType,
  Badge,
  ListPageSkeleton,
  StatsCard,
} from '@/components/ui';
import { apiService } from '@/services/api';
import { WhatsAppTemplate, TemplatesStats } from '@/modules/conversations/types';

interface WhatsAppConfig {
  id: string;
  phoneNumberId: string;
  businessAccountId: string;
  displayPhone: string;
  accountName: string | null;
  isPrimary: boolean;
  isActive: boolean;
}

interface AccountTemplates {
  account: {
    id: string;
    phoneNumberId: string;
    businessAccountId: string;
    displayPhone: string;
    accountName: string | null;
    isPrimary: boolean;
    isActive: boolean;
  };
  templates: WhatsAppTemplate[];
  stats: {
    total: number;
    approved: number;
    pending: number;
    rejected: number;
    totalUsage: number;
  };
}

export const WhatsAppTemplatesPage: React.FC = () => {
  const { t } = useTranslation();
  const [configs, setConfigs] = useState<WhatsAppConfig[]>([]);
  const [selectedConfigId, setSelectedConfigId] = useState<string>('all');
  const [accounts, setAccounts] = useState<AccountTemplates[]>([]);
  const [filteredTemplates, setFilteredTemplates] = useState<WhatsAppTemplate[]>([]);
  const [stats, setStats] = useState<TemplatesStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    category: 'all',
    language: 'all',
    sortBy: 'updatedAt' as 'name' | 'usageCount' | 'createdAt' | 'updatedAt',
    sortOrder: 'desc' as 'asc' | 'desc',
  });

  const loadConfigs = useCallback(async () => {
    try {
      const data = await apiService.getWhatsAppConfigs();
      setConfigs(data);

      // Auto-seleciona a conta primária ou a primeira
      if (data.length > 0) {
        const primary = data.find(c => c.isPrimary);
        setSelectedConfigId(primary?.id || data[0].id);
      }
    } catch (error: any) {
      console.error('Error loading configs:', error);
    }
  }, []);

  const loadTemplates = useCallback(async (configId?: string) => {
    try {
      setLoading(true);
      const data = await apiService.getWhatsAppTemplates(configId ? { configId } : undefined);

      // Se houver múltiplas contas
      if (data.accounts) {
        setAccounts(data.accounts);
        const allTemplates = data.accounts.flatMap(acc => acc.templates);
        setFilteredTemplates(allTemplates);
      }
      // Se houver apenas uma conta
      else if (data.account && data.templates && data.stats) {
        const singleAccount: AccountTemplates = {
          account: data.account,
          templates: data.templates,
          stats: data.stats,
        };
        setAccounts([singleAccount]);
        setFilteredTemplates(data.templates);
      }
    } catch (error: any) {
      console.error('Error loading templates:', error);
      setToast({
        message: error.message || t('conversations.errors.loadTemplatesError'),
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [t]);

  const loadStats = useCallback(async () => {
    try {
      const data = await apiService.getWhatsAppTemplatesStats();
      setStats(data);
    } catch (error: any) {
      console.error('Error loading stats:', error);
    }
  }, []);

  useEffect(() => {
    loadConfigs();
  }, [loadConfigs]);

  useEffect(() => {
    if (configs.length > 0) {
      const configId = selectedConfigId === 'all' ? undefined : selectedConfigId;
      loadTemplates(configId);
      loadStats();
    } else {
      setLoading(false);
    }
  }, [configs, selectedConfigId, loadTemplates, loadStats]);

  useEffect(() => {
    let filtered: WhatsAppTemplate[] = [];

    // Filtra templates baseado na conta selecionada
    if (selectedConfigId === 'all') {
      filtered = accounts.flatMap(acc => acc.templates);
    } else {
      const selectedAccount = accounts.find(acc => acc.account.id === selectedConfigId);
      filtered = selectedAccount?.templates || [];
    }

    // Aplica filtros
    if (filters.search) {
      filtered = filtered.filter((template) =>
        template.name.toLowerCase().includes(filters.search.toLowerCase())
      );
    }

    if (filters.status !== 'all') {
      filtered = filtered.filter((template) => template.status === filters.status);
    }

    if (filters.category !== 'all') {
      filtered = filtered.filter((template) => template.category === filters.category);
    }

    if (filters.language !== 'all') {
      filtered = filtered.filter((template) => template.language === filters.language);
    }

    // Aplica ordenação
    filtered.sort((a, b) => {
      const aVal = a[filters.sortBy] ?? '';
      const bVal = b[filters.sortBy] ?? '';

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return filters.sortOrder === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return filters.sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
      }

      return 0;
    });

    setFilteredTemplates(filtered);
  }, [filters, accounts, selectedConfigId]);

  const handleSync = async () => {
    try {
      setSyncing(true);
      await apiService.syncWhatsAppTemplates();
      setToast({
        message: t('whatsapp.success.templatesSynced'),
        type: 'success',
      });
      const configId = selectedConfigId === 'all' ? undefined : selectedConfigId;
      await loadTemplates(configId);
      await loadStats();
    } catch (error: any) {
      console.error('Error syncing templates:', error);
      setToast({
        message: error.message || t('conversations.templates.errors.syncError'),
        type: 'error',
      });
    } finally {
      setSyncing(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'APPROVED':
        return <Badge variant="success">{t('conversations.templates.status.APPROVED')}</Badge>;
      case 'PENDING':
        return <Badge variant="warning">{t('conversations.templates.status.PENDING')}</Badge>;
      case 'REJECTED':
        return <Badge variant="danger">{t('conversations.templates.status.REJECTED')}</Badge>;
      default:
        return <Badge variant="gray">{status}</Badge>;
    }
  };

  const getCategoryBadge = (category: string) => {
    switch (category) {
      case 'UTILITY':
        return <Badge variant="info">{t('conversations.templates.categories.UTILITY')}</Badge>;
      case 'MARKETING':
        return <Badge variant="primary">{t('conversations.templates.categories.MARKETING')}</Badge>;
      case 'AUTHENTICATION':
        return <Badge variant="success">{t('conversations.templates.categories.AUTHENTICATION')}</Badge>;
      default:
        return <Badge variant="gray">{category}</Badge>;
    }
  };

  const getTemplateBody = (template: WhatsAppTemplate): string => {
    const bodyComponent = template.components.find((c) => c.type === 'BODY');
    return bodyComponent?.text || '';
  };

  const getTotalStats = () => {
    if (selectedConfigId === 'all') {
      return accounts.reduce((acc, account) => ({
        total: acc.total + account.stats.total,
        approved: acc.approved + account.stats.approved,
        pending: acc.pending + account.stats.pending,
        rejected: acc.rejected + account.stats.rejected,
        totalUsage: acc.totalUsage + account.stats.totalUsage,
      }), { total: 0, approved: 0, pending: 0, rejected: 0, totalUsage: 0 });
    }

    const selectedAccount = accounts.find(acc => acc.account.id === selectedConfigId);
    return selectedAccount?.stats || { total: 0, approved: 0, pending: 0, rejected: 0, totalUsage: 0 };
  };

  if (loading) {
    return <ListPageSkeleton variant="cards" rows={6} showFilters={false} />;
  }

  if (configs.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] p-6">
        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
        <Card className="max-w-md">
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Phone className="h-16 w-16 text-muted-foreground mb-4" />
            <h2 className="text-xl font-semibold text-foreground mb-2">
              Nenhuma conta WhatsApp configurada
            </h2>
            <p className="text-sm text-muted-foreground mb-6">
              Para começar a usar templates, você precisa conectar uma conta WhatsApp Business primeiro.
            </p>
            <Button onClick={() => window.location.href = '/marketplace'}>
              Conectar WhatsApp
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const currentStats = getTotalStats();
  const selectedAccount = selectedConfigId !== 'all'
    ? accounts.find(acc => acc.account.id === selectedConfigId)
    : null;

  return (
    <div className="space-y-6 p-6">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Seletor de Conta */}
      {configs.length > 1 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2">
              <Phone className="h-5 w-5" />
              Contas WhatsApp Conectadas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <button
                onClick={() => setSelectedConfigId('all')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  selectedConfigId === 'all'
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <MessageSquare className="h-5 w-5 text-primary" />
                  <div className="text-left flex-1">
                    <p className="font-semibold">Todas as Contas</p>
                    <p className="text-xs text-muted-foreground">{configs.length} contas</p>
                  </div>
                </div>
              </button>

              {configs.map(config => (
                <button
                  key={config.id}
                  onClick={() => setSelectedConfigId(config.id)}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    selectedConfigId === config.id
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Phone className="h-5 w-5 text-primary" />
                    <div className="text-left flex-1">
                      <p className="font-semibold">{config.accountName || config.displayPhone}</p>
                      <p className="text-xs text-muted-foreground">{config.displayPhone}</p>
                      {config.isPrimary && (
                        <Badge variant="success" size="sm" className="mt-1">Principal</Badge>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dashboard de Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatsCard
            title="Total de Templates"
            value={currentStats.total}
            icon={MessageSquare}
            iconColor="text-primary"
            iconBg="bg-primary/10"
          />

          <StatsCard
            title="Templates Aprovados"
            value={currentStats.approved}
            icon={CheckCircle}
            iconColor="text-green-600"
            iconBg="bg-green-500/10"
            trend={
              currentStats.approved > 0
                ? { value: Math.round((currentStats.approved / currentStats.total) * 100), isPositive: true }
                : undefined
            }
          />

          <StatsCard
            title="Aguardando Aprovação"
            value={currentStats.pending}
            icon={Clock}
            iconColor="text-yellow-600"
            iconBg="bg-yellow-500/10"
          />

          <StatsCard
            title="Total de Envios"
            value={currentStats.totalUsage.toLocaleString('pt-BR')}
            icon={Send}
            iconColor="text-blue-600"
            iconBg="bg-blue-500/10"
          />
        </div>
      )}

      {/* Top Templates */}
      {stats && stats.topByUsage.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-accent" />
              Templates Mais Usados
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.topByUsage.map((template, index) => (
                <div
                  key={template.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                >
                  <div className="flex items-center gap-3 flex-1">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-accent/10 text-accent flex items-center justify-center font-bold text-sm">
                      #{index + 1}
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-foreground truncate">
                        {template.name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {template.category}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="info" size="sm">
                      {template.usageCount} envios
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <MessageSquare className="h-6 w-6" />
            {t('whatsapp.templates.title')}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {filteredTemplates.length} templates • {currentStats.totalUsage} envios
            {selectedAccount && ` • ${selectedAccount.account.displayPhone}`}
          </p>
        </div>
        <Button onClick={handleSync} disabled={syncing}>
          <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
          {syncing ? t('common.syncing') : t('whatsapp.templates.sync')}
        </Button>
      </div>

      {/* Filtros */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            {t('common.filters')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                placeholder={t('conversations.templates.searchPlaceholder')}
                className="pl-10"
              />
            </div>

            <Select
              value={filters.status}
              onValueChange={(value) => setFilters({ ...filters, status: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('common.status')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('common.all')}</SelectItem>
                <SelectItem value="APPROVED">{t('conversations.templates.status.APPROVED')}</SelectItem>
                <SelectItem value="PENDING">{t('conversations.templates.status.PENDING')}</SelectItem>
                <SelectItem value="REJECTED">{t('conversations.templates.status.REJECTED')}</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={filters.category}
              onValueChange={(value) => setFilters({ ...filters, category: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('conversations.templates.category')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('common.all')}</SelectItem>
                <SelectItem value="UTILITY">{t('conversations.templates.categories.UTILITY')}</SelectItem>
                <SelectItem value="MARKETING">{t('conversations.templates.categories.MARKETING')}</SelectItem>
                <SelectItem value="AUTHENTICATION">{t('conversations.templates.categories.AUTHENTICATION')}</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={filters.language}
              onValueChange={(value) => setFilters({ ...filters, language: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('conversations.templates.language')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('common.all')}</SelectItem>
                <SelectItem value="pt_BR">Português (BR)</SelectItem>
                <SelectItem value="en_US">English (US)</SelectItem>
                <SelectItem value="es">Español</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={filters.sortBy}
              onValueChange={(value: any) => setFilters({ ...filters, sortBy: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name">Nome</SelectItem>
                <SelectItem value="usageCount">Mais Usados</SelectItem>
                <SelectItem value="createdAt">Data de Criação</SelectItem>
                <SelectItem value="updatedAt">Atualização</SelectItem>
              </SelectContent>
            </Select>

            <Button
              variant="outline"
              size="icon"
              onClick={() => setFilters({
                ...filters,
                sortOrder: filters.sortOrder === 'asc' ? 'desc' : 'asc'
              })}
            >
              {filters.sortOrder === 'asc' ? (
                <ArrowUpAZ className="h-4 w-4" />
              ) : (
                <ArrowDownZA className="h-4 w-4" />
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Header da Conta Selecionada */}
      {selectedAccount && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-lg bg-primary/10 text-primary">
                  <Phone className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold">
                    Conta: {selectedAccount.account.accountName || selectedAccount.account.displayPhone}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    ID: {selectedAccount.account.businessAccountId}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-foreground">
                    {selectedAccount.stats.approved}
                  </p>
                  <p className="text-xs text-muted-foreground">Aprovados</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-yellow-600">
                    {selectedAccount.stats.pending}
                  </p>
                  <p className="text-xs text-muted-foreground">Pendentes</p>
                </div>
                {selectedAccount.stats.rejected > 0 && (
                  <div className="text-center">
                    <p className="text-2xl font-bold text-red-600">
                      {selectedAccount.stats.rejected}
                    </p>
                    <p className="text-xs text-muted-foreground">Rejeitados</p>
                  </div>
                )}
              </div>
            </div>
          </CardHeader>
        </Card>
      )}

      {/* Grid de Templates */}
      <div className="grid grid-cols-1 gap-4">
        {filteredTemplates.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <MessageSquare className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-lg font-medium text-foreground">
                {t('conversations.templates.noTemplates')}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                {t('whatsapp.templates.syncHint')}
              </p>
            </CardContent>
          </Card>
        ) : (
          filteredTemplates.map((template) => (
            <Card key={template.id}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <h3 className="text-lg font-semibold text-foreground">{template.name}</h3>

                      {template.accountInfo && (
                        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-primary/10 text-primary border border-primary/20">
                          <Phone className="h-3.5 w-3.5" />
                          <span className="text-xs font-medium">
                            {template.accountInfo.displayPhone}
                          </span>
                        </div>
                      )}

                      {getStatusBadge(template.status)}
                      {getCategoryBadge(template.category)}
                      <Badge variant="gray">{template.language}</Badge>
                      {template.isActive && <Badge variant="success">{t('common.active')}</Badge>}
                    </div>

                    <div className="bg-muted/50 border border-border rounded-lg p-4 mt-3">
                      <p className="text-xs font-medium text-muted-foreground mb-2">
                        {t('conversations.newConversation.preview')}:
                      </p>
                      <p className="text-sm text-foreground whitespace-pre-wrap">
                        {getTemplateBody(template)}
                      </p>
                    </div>

                    {template.usageCount !== undefined && (
                      <div className="mt-3 text-sm text-muted-foreground">
                        {t('whatsapp.templates.usageCount')}: {template.usageCount}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};
